// resources/js/Components/ZonesManager.jsx
import React, {useEffect, useState, useCallback, useRef, useMemo} from 'react';
import { flushSync } from 'react-dom';
import {Badge, Grid, IconButton, Tooltip, Typography} from '@mui/material';
import ZoneCard from "../zone/ZoneCard";
import LoadingCard from "../common/LoadingCard";
import ScheduleDrawer from "../zone/ScheduleDrawer";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {styled} from "@mui/material/styles";
import {badgeClasses} from "@mui/material/Badge";
import cloneDeep from 'lodash/cloneDeep';
import ZoneAdvancedSettingsModal from '../zone/ZoneAdvancedSettingsModal';

/**
 * Ensures legacy payloads (objects, JSON strings) become arrays before React consumes them.
 */
const normalizeToArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return [];
        }
    }

    if (value && typeof value === 'object') {
        return Object.values(value);
    }

    return [];
};

/**
 * Flattens any id collection into a string array so includes/indexOf checks remain stable.
 */
const normalizeIdList = (value) => normalizeToArray(value)
    .map((entry) => {
        if (entry == null) {
            return null;
        }

        if (typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'id')) {
            return entry.id != null ? entry.id.toString() : null;
        }

        return entry?.toString?.() ?? null;
    })
    .filter(Boolean);

const normalizeMarkerSets = (value) => normalizeToArray(value).map((entry = {}) => {
    const normalizedId = entry?.id != null ? entry.id.toString() : '';
    return {
        ...entry,
        id: normalizedId,
        name: entry?.name || `Marker Set ${normalizedId || ''}`,
        type: entry?.type || null,
        color_key: entry?.color_key || entry?.colorKey || null,
    };
});

const normalizeZoneReper = (value) => {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const point = Array.isArray(value?.point) ? value.point : [];
    const x = Number(point[0] ?? point?.x);
    const y = Number(point[1] ?? point?.y);
    const range = Number(value?.range ?? value?.reper_range ?? value?.radius_scaled);

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(range)) {
        return {};
    }

    return {
        point: [Number.parseFloat(x.toFixed(6)), Number.parseFloat(y.toFixed(6))],
        range: Number.parseFloat(range.toFixed(6)),
    };
};

const clampDirectionCoordinate = (value, fallback = 0.5) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return fallback;
    }

    return Number.parseFloat(Math.min(Math.max(numeric, 0), 1).toFixed(6));
};

const normalizeZoneDirection = (value) => {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const start = Array.isArray(value?.start) ? value.start : [];
    const stop = Array.isArray(value?.stop) ? value.stop : [];
    const startX = Number(start[0] ?? start?.x);
    const startY = Number(start[1] ?? start?.y);
    const stopX = Number(stop[0] ?? stop?.x);
    const stopY = Number(stop[1] ?? stop?.y);

    if (!Number.isFinite(startX) || !Number.isFinite(startY) || !Number.isFinite(stopX) || !Number.isFinite(stopY)) {
        return {};
    }

    const countsDirSource = value?.counts_dir && typeof value.counts_dir === 'object' ? value.counts_dir : {};
    const countsDir = Object.entries(countsDirSource).reduce((carry, [className, mode]) => {
        if (typeof className !== 'string' || className.trim() === '') {
            return carry;
        }

        if (!['forward', 'backward', 'both'].includes(mode)) {
            return carry;
        }

        carry[className] = mode;
        return carry;
    }, {});

    return {
        start: [clampDirectionCoordinate(startX, 0.25), clampDirectionCoordinate(startY, 0.5)],
        stop: [clampDirectionCoordinate(stopX, 0.75), clampDirectionCoordinate(stopY, 0.5)],
        counts_dir: countsDir,
    };
};

const normalizeZoneGpio = (value) => {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const id = Number(value?.id);
    const name = typeof value?.name === 'string' ? value.name.trim() : '';
    const delayValue = Number(value?.delay ?? 0);
    const classes = Array.isArray(value?.classes)
        ? value.classes.map((entry) => (typeof entry === 'string' ? entry.trim() : '')).filter(Boolean)
        : [];
    const freeClasses = Array.isArray(value?.free_classes)
        ? value.free_classes.map((entry) => (typeof entry === 'string' ? entry.trim() : '')).filter(Boolean)
        : [];

    if (!Number.isFinite(id) || id <= 0 || !name) {
        return {};
    }

    return {
        id,
        name,
        delay: Number.isFinite(delayValue) && delayValue >= 0 ? Number(delayValue.toFixed(2)) : 0,
        classes: Array.from(new Set(classes)),
        free_classes: Array.from(new Set(freeClasses)),
    };
};

const resolveZoneReper = (zone) => {
    const directReper = normalizeZoneReper(zone?.reper);
    if (Array.isArray(directReper.point) && directReper.point.length === 2 && Number.isFinite(Number(directReper.range))) {
        return directReper;
    }

    return normalizeZoneReper(zone?.meta?.reper);
};

const resolveZoneDirection = (zone) => {
    const directDirection = normalizeZoneDirection(zone?.direction);
    if (Array.isArray(directDirection.start) && directDirection.start.length === 2 && Array.isArray(directDirection.stop) && directDirection.stop.length === 2) {
        return directDirection;
    }

    return normalizeZoneDirection(zone?.meta?.direction);
};

const readStoredEnhancedAnalyzerReper = () => {
    const input = document.getElementById('enhancedAnalyzerConfig');
    if (!input || typeof input.value !== 'string' || input.value.trim() === '') {
        return {};
    }

    try {
        const payload = JSON.parse(input.value);
        const center = payload?.center && typeof payload.center === 'object' ? payload.center : {};

        return normalizeZoneReper({
            point: [center?.x_scaled, center?.y_scaled],
            range: payload?.reper_range ?? payload?.radius_scaled,
        });
    } catch (error) {
        return {};
    }
};

const hasZoneReper = (zone) => {
    const reper = resolveZoneReper(zone);
    return Array.isArray(reper.point) && reper.point.length === 2 && Number.isFinite(Number(reper.range));
};

const hasZoneDirection = (zone) => {
    const direction = resolveZoneDirection(zone);
    return Array.isArray(direction.start) && direction.start.length === 2 && Array.isArray(direction.stop) && direction.stop.length === 2;
};

const buildZoneMeta = (zone) => {
    const meta = zone?.meta && typeof zone.meta === 'object' ? cloneDeep(zone.meta) : {};
    meta.reper = resolveZoneReper({ ...zone, meta });
    meta.direction = resolveZoneDirection({ ...zone, meta });
    meta.gpio = normalizeZoneGpio(zone?.gpio ?? meta?.gpio);
    return meta;
};

const parseAdvancedSettings = (payload) => {
    if (!payload) {
        return {};
    }
    if (typeof payload === 'string') {
        try {
            return JSON.parse(payload) || {};
        } catch (error) {
            console.warn('Failed to parse advanced_settings in ZonesManager', error);
            return {};
        }
    }
    if (typeof payload === 'object') {
        return payload;
    }
    return {};
};

const extractMarkerSetId = (analyzer = {}) => {
    if (!analyzer || typeof analyzer !== 'object') {
        return null;
    }
    const candidate = analyzer.marker_set_id ?? analyzer.marker_set ?? analyzer.markerSet?.id ?? analyzer.markerSet?.value;
    if (candidate === null || typeof candidate === 'undefined' || candidate === '') {
        return null;
    }
    return candidate.toString();
};

const buildMarkerSetUsage = (zones = []) => {
    return zones.reduce((usage, zone, zoneIndex) => {
        const advanced = parseAdvancedSettings(zone?.advanced_settings);
        const analyzers = Array.isArray(advanced?.analyzers) ? advanced.analyzers : [];

        analyzers.forEach((analyzer) => {
            const markerSetId = extractMarkerSetId(analyzer);
            if (!markerSetId) {
                return;
            }
            if (!usage[markerSetId]) {
                usage[markerSetId] = {
                    count: 0,
                    zones: [],
                };
            }
            usage[markerSetId].count += 1;
            usage[markerSetId].zones.push({
                zoneId: zone?.id ?? null,
                zoneIndex,
                zoneName: zone?.name || `Zone ${zoneIndex + 1}`,
                analyzerName: analyzer?.name || analyzer?.model || 'Analyzer',
            });
        });

        return usage;
    }, {});
};

/**
 * Normalizes numeric fields for managed alarm payloads posted through Blade.
 */
const parseAlarmNumber = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const parseTransportLink = (value = '') => {
    const normalized = String(value || '');
    const match = normalized.match(/^transport@(\d+):(.*)$/);
    if (!match) {
        return null;
    }

    return {
        deviceId: match[1],
        streamUrl: match[2] || '',
    };
};

const ZonesManager = () => {
    const [zones, setZones] = useState([]);
    const [allTags, setAllTags] = useState([]);
    const [trackedTags, setTrackedTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [fixedTags, setFixedTags] = useState([]);
    const [markerSets, setMarkerSets] = useState([]);
    const [gpioGates, setGpioGates] = useState([]);
    const [trafficLights, setTrafficLights] = useState([]);
    const [selectedModel, setSelectedModel] = useState('multi2_640_300.engine');
    const [captioningFlag, setCaptioningFlag] = useState(false);
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [activeSchedule, setActiveSchedule] = useState(null);
    const [armedData, setArmedData] = useState([]);
    const [disabled, setDisabled] = useState(false);
    const [alarmOverrideEnabled, setAlarmOverrideEnabled] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [focusedZoneIndex, setFocusedZoneIndex] = useState(null);
    const [focusEventId, setFocusEventId] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [managerConfig, setManagerConfig] = useState({});
    const [selectedHornId, setSelectedHornId] = useState(() => {
        if (typeof document === 'undefined') {
            return -1;
        }

        const directInputValue = document.getElementById('horn_id')?.value;
        const linkSettingsRoot = document.getElementById('link-settings-root');
        const datasetValue = linkSettingsRoot?.dataset?.initialHornId;
        const parsed = Number.parseInt(directInputValue || datasetValue || '-1', 10);
        return Number.isFinite(parsed) ? parsed : -1;
    });
    const [zoneAudioMessageEnabled, setZoneAudioMessageEnabled] = useState(() => {
        if (typeof document === 'undefined') {
            return false;
        }

        const linkSettingsRoot = document.getElementById('link-settings-root');
        const initialLink = linkSettingsRoot?.dataset?.initialLink || '';
        const initialUploadedLink = linkSettingsRoot?.dataset?.initialUploadedLink || '0';

        if (parseTransportLink(initialLink)) {
            return true;
        }

        return String(initialUploadedLink) !== '1';
    });
    const hasBroadcastInitialLoad = useRef(false);
    const zonesRef = useRef([]);
    const lastDirectionByZoneRef = useRef({});

    useEffect(() => {
        zonesRef.current = zones;
    }, [zones]);

    useEffect(() => {
        if (focusedZoneIndex !== null && focusedZoneIndex >= zones.length) {
            setFocusedZoneIndex(null);
        }
    }, [focusedZoneIndex, zones.length]);

    const getCanvasDimensions = useCallback(() => {
        const instance = window?.editPlayer;
        const width = Number(instance?.config?.video_width) || Number(window?._canvas?.backgroundImage?.width);
        const height = Number(instance?.config?.video_height) || Number(window?._canvas?.backgroundImage?.height);

        return {
            width: Number.isFinite(width) && width > 0 ? width : null,
            height: Number.isFinite(height) && height > 0 ? height : null,
        };
    }, []);

    const serializePoints = useCallback((points = []) => {
        return points.map((point) => {
            const rawX = point?.x ?? point?.left ?? 0;
            const rawY = point?.y ?? point?.top ?? 0;
            const numericX = Number(rawX);
            const numericY = Number(rawY);

            return {
                x: Number.isFinite(numericX) ? Number.parseFloat(numericX.toFixed(4)) : 0,
                y: Number.isFinite(numericY) ? Number.parseFloat(numericY.toFixed(4)) : 0,
            };
        });
    }, []);

    const applyScaledMetadata = useCallback((zone, points = []) => {
        const serialized = serializePoints(points);
        const { width, height } = getCanvasDimensions();

        if (!width || !height) {
            return {
                ...zone,
                polygon: serialized,
            };
        }

        const scaled = serialized.map((point) => ({
            x: Number.parseFloat((point.x / width).toFixed(4)),
            y: Number.parseFloat((point.y / height).toFixed(4)),
        }));

        return {
            ...zone,
            polygon: serialized,
            scaled_polygon: scaled,
            original_width: width,
            original_height: height,
            version: 2,
        };
    }, [getCanvasDimensions, serializePoints]);

    const buildDefaultZoneDirection = useCallback((zone) => {
        const scaledPolygon = Array.isArray(zone?.scaled_polygon) ? zone.scaled_polygon : [];
        let normalizedPoints = scaledPolygon
            .map((point) => {
                const x = Number(point?.x ?? point?.[0]);
                const y = Number(point?.y ?? point?.[1]);
                if (!Number.isFinite(x) || !Number.isFinite(y)) {
                    return null;
                }

                return {
                    x: clampDirectionCoordinate(x),
                    y: clampDirectionCoordinate(y),
                };
            })
            .filter(Boolean);

        if (!normalizedPoints.length) {
            const { width, height } = getCanvasDimensions();
            const sourceWidth = Number(zone?.original_width ?? width);
            const sourceHeight = Number(zone?.original_height ?? height);
            const polygon = Array.isArray(zone?.polygon) ? zone.polygon : [];

            if (sourceWidth > 0 && sourceHeight > 0) {
                normalizedPoints = polygon.map((point) => {
                    const x = Number(point?.x ?? point?.[0]);
                    const y = Number(point?.y ?? point?.[1]);
                    if (!Number.isFinite(x) || !Number.isFinite(y)) {
                        return null;
                    }

                    return {
                        x: clampDirectionCoordinate(x / sourceWidth),
                        y: clampDirectionCoordinate(y / sourceHeight),
                    };
                }).filter(Boolean);
            }
        }

        if (!normalizedPoints.length) {
            return {
                start: [0.25, 0.5],
                stop: [0.75, 0.5],
                counts_dir: {},
            };
        }

        const xs = normalizedPoints.map((point) => point.x);
        const ys = normalizedPoints.map((point) => point.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const width = Math.max(maxX - minX, 0.1);
        const centerY = minY + ((maxY - minY) / 2);

        return {
            start: [clampDirectionCoordinate(minX + (width * 0.25), 0.25), clampDirectionCoordinate(centerY, 0.5)],
            stop: [clampDirectionCoordinate(minX + (width * 0.75), 0.75), clampDirectionCoordinate(centerY, 0.5)],
            counts_dir: resolveZoneDirection(zone)?.counts_dir || {},
        };
    }, [getCanvasDimensions]);

    useEffect(() => {
        // Signal that the component has mounted and rendered
        window.dispatchEvent(new CustomEvent("zoneEditorManager:loaded"));
    }, []); // Empty dependency array ensures this runs only once on mount

    // State for managing the Advanced Settings Modal
    const [advancedModalState, setAdvancedModalState] = useState({
        show: false,
        zoneIndex: null,
        zoneData: null,
    });

    const markerSetUsage = useMemo(() => buildMarkerSetUsage(zones), [zones]);

    const handleToggle = () => {
        setExpanded((prev) => {
            const next = !prev;
            if (next) {
                setFocusedZoneIndex(null);
            }
            return next;
        });
    }

    const CartBadge = styled(Badge)`
        & .${badgeClasses.badge} {
            top: -12px;
            right: -6px;
        }
    `;

    // Drawer Save Handler
    const handleScheduleSave = (data) => {

        if (activeSchedule !== null) {
            setZones((prevZones) => {
                const updatedZones = [...prevZones];
                updatedZones[activeSchedule].armed = data;
                return updatedZones;
            });
            setArmedData([]);
            setActiveSchedule(null);
        }

    }

    const triggerZoneFocus = useCallback((zoneIndex) => {
        if (!Number.isInteger(zoneIndex) || zoneIndex < 0) {
            return;
        }
        setExpanded(false);
        setFocusedZoneIndex(zoneIndex);
        setFocusEventId((prev) => prev + 1);
    }, []);

    const handleCardExpandChange = useCallback((zoneIndex, isExpanded) => {
        setFocusedZoneIndex((prev) => {
            if (prev === null) {
                return prev;
            }
            if (prev === zoneIndex && !isExpanded) {
                return null;
            }
            if (prev !== zoneIndex && isExpanded) {
                return null;
            }
            return prev;
        });
    }, []);

    const handleToggleReperZone = useCallback((zoneIndex) => {
        const currentZones = zonesRef.current || [];
        const previousZone = currentZones.find((zone) => hasZoneReper(zone)) || null;
        const storedReper = readStoredEnhancedAnalyzerReper();
        const nextReper = previousZone
            ? resolveZoneReper(previousZone)
            : (Array.isArray(storedReper.point) && storedReper.point.length === 2 ? storedReper : { point: [0.5, 0.5], range: 0.15 });
        const targetAlreadySelected = hasZoneReper(currentZones[zoneIndex]);

        setZones((prevZones) => prevZones.map((zone, index) => {
            const nextZone = { ...zone };
            const nextMeta = buildZoneMeta(nextZone);

            if (index === zoneIndex) {
                nextMeta.reper = targetAlreadySelected ? {} : nextReper;
                nextZone.reper = nextMeta.reper;
                nextZone.meta = nextMeta;
                return nextZone;
            }

            // Current product contract allows only one active reper zone.
            // When multi-zone reper support is enabled later, remove this clearing branch
            // and store multiple populated `zone.meta.reper` objects instead.
            nextMeta.reper = {};
            nextZone.reper = {};
            nextZone.meta = nextMeta;
            return nextZone;
        }));

        if (!targetAlreadySelected) {
            triggerZoneFocus(zoneIndex);
        }
    }, [triggerZoneFocus]);

    const handleToggleDirectionZone = useCallback((zoneIndex, enabled = null) => {
        const currentZones = zonesRef.current || [];
        const targetZone = currentZones[zoneIndex];
        if (!targetZone) {
            return;
        }

        const currentlyEnabled = hasZoneDirection(targetZone);
        const shouldEnable = typeof enabled === 'boolean' ? enabled : !currentlyEnabled;

        setZones((prevZones) => prevZones.map((zone, index) => {
            if (index !== zoneIndex) {
                return zone;
            }

            const nextZone = { ...zone };
            const nextMeta = buildZoneMeta(nextZone);
            const storedDirection = lastDirectionByZoneRef.current[zoneIndex];
            const nextDirection = shouldEnable
                ? (currentlyEnabled
                    ? resolveZoneDirection(nextZone)
                    : (hasZoneDirection(storedDirection) ? storedDirection : buildDefaultZoneDirection(nextZone)))
                : {};

            if (currentlyEnabled) {
                lastDirectionByZoneRef.current[zoneIndex] = resolveZoneDirection(nextZone);
            }

            nextMeta.direction = nextDirection;
            nextZone.direction = nextDirection;
            nextZone.meta = nextMeta;
            return nextZone;
        }));

        if (shouldEnable) {
            triggerZoneFocus(zoneIndex);
            return;
        }

        window.dispatchEvent(new CustomEvent('zones:direction:clear-request', { detail: { zoneIndex } }));
    }, [buildDefaultZoneDirection, triggerZoneFocus]);

    const handleEditDirectionZone = useCallback((zoneIndex) => {
        const currentZones = zonesRef.current || [];
        const targetZone = currentZones[zoneIndex];
        if (!targetZone) {
            return;
        }

        if (!hasZoneDirection(targetZone)) {
            handleToggleDirectionZone(zoneIndex, true);
        } else {
            triggerZoneFocus(zoneIndex);
        }

        window.dispatchEvent(new CustomEvent('zones:direction:select-request', { detail: { zoneIndex } }));
    }, [handleToggleDirectionZone, triggerZoneFocus]);

    // --- Delete Zone Handler ---
    const handleDeleteZone = (zoneIndex) => {
        const zoneToDelete = zones[zoneIndex];
        if (!zoneToDelete) return;
        const confirmed = confirm('Are you sure you want to delete this zone?');
        if (confirmed) {
            // Dispatch the delete event FIRST using the originalIndex
            window.dispatchEvent(new CustomEvent("zones:updated", { detail: { action: "deleted", index: zoneToDelete.originalIndex } }));
            // Then update React state
            setZones((prevZones) => prevZones.filter((_, i) => i !== zoneIndex));
        }
    };

    // --- Clone Zone Handler ---
    const handleCloneZone = (zoneIndex) => {
        const zoneToClone = zones[zoneIndex];
        if (!zoneToClone) return;
        const confirmed = confirm('Are you sure you want to clone this zone?');
        if (confirmed) {
            let clonedZoneDataForEvent;
            setZones((prevZones) => {
                // Use the zone from the current state for cloning
                clonedZoneDataForEvent = cloneDeep(zoneToClone);
                 // Assign a new originalIndex (e.g., based on length or a temporary negative ID)
                 // This needs careful handling if backend IDs are involved later.
                 // For now, using length might suffice if zones aren't deleted before save.
                clonedZoneDataForEvent.originalIndex = prevZones.length;
                clonedZoneDataForEvent.id = null; // Ensure no backend ID initially
                clonedZoneDataForEvent.polygon = clonedZoneDataForEvent.polygon || clonedZoneDataForEvent.points || [];
                clonedZoneDataForEvent.name = `Zone ${prevZones.length + 1} (Clone)`;

                return [...prevZones, clonedZoneDataForEvent]; // Append the clone
            });

            // Dispatch event AFTER state update, passing necessary data for canvas
            // Note: index here is the *original* index being cloned
            if (clonedZoneDataForEvent) {
                 window.dispatchEvent(new CustomEvent("zones:updated", {
                    detail: {
                        action: "cloned",
                        index: zoneIndex, // Original React index cloned from
                        zoneData: { // Data needed by canvas listener
                            points: clonedZoneDataForEvent.polygon,
                            type: clonedZoneDataForEvent.type,
                            name: clonedZoneDataForEvent.name
                        }
                    }
                 }));
            } else {
                console.error("Cloned zone data was not available for dispatching event.");
            }
        }
    };

    // Load initial zones from data-zones attribute
    useEffect(() => {
        const container = document.getElementById('react-zones');
        if (container) {
            const rawZones = JSON.parse(container.dataset.zones || '[]');
            // Add originalIndex and ensure consistent structure
            const initialZones = rawZones.map((zone, index) => {
                const polygon = Array.isArray(zone?.polygon) ? zone.polygon : [];
                const scaledPolygon = Array.isArray(zone?.scaled_polygon) ? zone.scaled_polygon : [];

                return {
                    ...zone,
                    polygon,
                    scaled_polygon: scaledPolygon,
                    meta: zone?.meta && typeof zone.meta === 'object' ? zone.meta : {},
                    reper: normalizeZoneReper(zone?.reper ?? zone?.meta?.reper),
                    direction: normalizeZoneDirection(zone?.direction ?? zone?.meta?.direction),
                    gpio: normalizeZoneGpio(zone?.gpio ?? zone?.meta?.gpio),
                    original_width: zone?.original_width ?? null,
                    original_height: zone?.original_height ?? null,
                    version: zone?.version ?? (scaledPolygon.length ? 2 : null),
                    // Use zone.id from backend if it exists and is reliable, otherwise use array index.
                    // Assuming polygons.js index starts at 0 and matches backend order if applicable.
                    originalIndex: zone?.original_index ?? index, // *** Store original index ***
                };
            });
            setZones(initialZones);
            const initialTrackedTags = normalizeIdList(JSON.parse(container.dataset.trackedTags || '[]'));
            console.log("Initial tracked tags:", initialTrackedTags);
            setTrackedTags(initialTrackedTags);
            const initialSelectedTags = normalizeIdList(JSON.parse(container.dataset.selectedTags || '[]'));
            setSelectedTags(initialSelectedTags);
            const initialFixedTags = normalizeIdList(JSON.parse(container.dataset.fixedTags || '[]'));
            setFixedTags(initialFixedTags);
            console.log("Initial selected tags:", initialSelectedTags);
            const initialDisabled = JSON.parse(container.dataset.disabled || 'false');
            setDisabled(initialDisabled);
            setAlarmOverrideEnabled(container.dataset.alarmOverrideEnabled === 'true');
            const isAdmin = container.dataset.isAdmin === 'true';

            // Read additional config from data attributes
            const tagsUrl = container.dataset.tagsUrl || '';
            let analyzerModels = [];
            let detectorModels = [];
            let availableModels = [];
            let initialMarkerSets = [];
            const markerSetsUrl = container.dataset.markerSetsUrl || '';
            try {
                analyzerModels = normalizeToArray(JSON.parse(container.dataset.analyzerModels || '{}'));
            } catch (e) { console.error("Error parsing analyzerModels:", e); analyzerModels = []; }
            try {
                detectorModels = normalizeToArray(JSON.parse(container.dataset.detectorModels || '{}'));
            } catch (e) { console.error("Error parsing detectorModels:", e); detectorModels = []; }
            try {
                availableModels = normalizeToArray(JSON.parse(container.dataset.availableModels || '{}'));
            } catch (e) { console.error("Error parsing availableModels:", e); availableModels = []; }
            try {
                initialMarkerSets = normalizeMarkerSets(JSON.parse(container.dataset.markerSets || '[]'));
            } catch (e) { console.error("Error parsing markerSets:", e); initialMarkerSets = []; }
            try {
                setGpioGates(normalizeToArray(JSON.parse(container.dataset.gpioGates || '[]')));
                setTrafficLights(normalizeToArray(JSON.parse(container.dataset.trafficLights || '[]')));
            } catch (e) { console.error("Error parsing gpio gates:", e); setGpioGates([]); }

            // Set the combined config state
            const newConfig = {
                tagsUrl: tagsUrl,
                analyzerModels: analyzerModels,
                detectorModels: detectorModels,
                availableModels: availableModels,
                markerSetsUrl: markerSetsUrl,
                isAdmin,
                // Add main page tag selections for Union logic
                mainPageTrackedTags: initialTrackedTags,
                mainPageSelectedTags: initialSelectedTags,
                // Include any other config needed from dataset.config if it existed
                // ...JSON.parse(container.dataset.config || '{}')
            };
            setManagerConfig(newConfig);
            console.log("Manager Config set:", newConfig);
            setMarkerSets(initialMarkerSets);

            // Saved choices are sufficient for readonly forms, including an empty set.
            if (container.dataset.fullEditOptions === 'false') {
                const initialTags = normalizeToArray(JSON.parse(container.dataset.initialTags || '[]'));
                setAllTags(initialTags);
                window.__lastAllTagsPayload = initialTags;
                window.dispatchEvent(new CustomEvent('allTags:updated', { detail: initialTags }));
            } else {
                fetchAllTags(newConfig.tagsUrl);
            }
        }
        setIsLoading(false);
    }, []);

    useEffect(()=>{
        if (!hasBroadcastInitialLoad.current && !isLoading) {
            window.dispatchEvent(new CustomEvent("zones:manager:loaded", {detail: {zones}}));
            hasBroadcastInitialLoad.current = true;
        }
    },[zones, isLoading]);

    useEffect(() => {
        if (!isLoading) {
            window.dispatchEvent(new CustomEvent("zones:metadata:update", { detail: { zones } }));
        }
    }, [zones, isLoading]);

    // Fetch all tags (example implementation - adapt to your API)
    const fetchAllTags = useCallback(async (tagsUrl) => {
        if (document.getElementById('react-zones')?.dataset.fullEditOptions === 'false') return;
        if (!tagsUrl) return;
        try {
            const response = await fetch(`${tagsUrl}?model=`); // Assuming empty model fetches all
            const data = await response.json();
            setAllTags(normalizeToArray(data.tags));
        } catch (error) {
            console.error('Error fetching all tags:', error);
        }
    }, []);

    useEffect(() => {
        if (!managerConfig.markerSetsUrl) {
            return undefined;
        }

        let cancelled = false;
        const fetchMarkerSets = async () => {
            try {
                const response = await fetch(managerConfig.markerSetsUrl, {
                    headers: { Accept: 'application/json' },
                });
                if (!response.ok) {
                    throw new Error(`Failed to load marker sets (${response.status})`);
                }
                const payload = await response.json();
                if (!cancelled) {
                    const normalized = normalizeMarkerSets(payload?.data || payload);
                    setMarkerSets(normalized);
                }
            } catch (error) {
                console.error('Error fetching marker sets:', error);
            }
        };

        fetchMarkerSets();
        return () => {
            cancelled = true;
        };
    }, [managerConfig.markerSetsUrl]);

    useEffect(() => {
        const handleMarkersRender = (event) => {
            const nextSets = normalizeMarkerSets(event?.detail?.markerSets || []);
            setMarkerSets(nextSets);
        };

        window.addEventListener('markers:render', handleMarkersRender);
        return () => {
            window.removeEventListener('markers:render', handleMarkersRender);
        };
    }, []);

    useEffect(() => {
        window.dispatchEvent(new CustomEvent('markers:usage:update', { detail: markerSetUsage }));
    }, [markerSetUsage]);

    useEffect(() => {
        const handleUsageRequest = () => {
            window.dispatchEvent(new CustomEvent('markers:usage:update', { detail: markerSetUsage }));
        };

        window.addEventListener('markers:usage:request', handleUsageRequest);
        return () => {
            window.removeEventListener('markers:usage:request', handleUsageRequest);
        };
    }, [markerSetUsage]);

    useEffect(() => {
        const handleAlarmOverrideUpdated = (event) => {
            setAlarmOverrideEnabled(Boolean(event?.detail?.enabled));
        };

        window.addEventListener('item:alarm-override:updated', handleAlarmOverrideUpdated);
        return () => {
            window.removeEventListener('item:alarm-override:updated', handleAlarmOverrideUpdated);
        };
    }, []);

    // Listen for new zones via window event
    useEffect(() => {
        // Listener for updates dispatched from editVideoPlayer.js or polygons.js
        const handleZonesUpdated = (event) => {
            const { action, index, poligon } = event.detail; // `index` here is the polygon.js internal index
            console.log("ZonesManager received zones:updated:", action, "polygon.js index:", poligon?.index, poligon);

            if (!poligon && (action === 'add' || action === 'modified')) {
                 console.error(`Action '${action}' received without poligon data.`);
                 return;
            }

            setZones(prevZones => {
                const polygonJsIndex = poligon?.index; // Index assigned by polygons.js

                // Find existing zone based on the index assigned by polygons.js
                const existingZoneStateIndex = prevZones.findIndex(zone => zone.originalIndex === polygonJsIndex);

                const polygonPoints = poligon?.poligonPoints || [];

                if (action === 'add') {
                    if (existingZoneStateIndex !== -1) {
                        // --- UPDATE EXISTING ZONE --- (Called by addZone after modification)
                        console.log(`Updating existing zone points (originalIndex: ${polygonJsIndex}) at state index: ${existingZoneStateIndex}`);
                        const updatedZones = [...prevZones];
                        const enrichedZone = applyScaledMetadata(updatedZones[existingZoneStateIndex], polygonPoints);
                        updatedZones[existingZoneStateIndex] = {
                            ...enrichedZone,
                            name: poligon.name || enrichedZone.name,
                            type: poligon.type || enrichedZone.type,
                        };
                        return updatedZones;
                    } else {
                        // --- ADD NEW ZONE --- (Called by addZone after initial creation)
                        console.log(`Adding new zone with originalIndex: ${polygonJsIndex}`);
                        const baseZone = {
                            id: null, // No backend ID yet
                            originalIndex: polygonJsIndex, // Store the index from polygons.js
                            original_index: polygonJsIndex,
                            name: poligon.name || `Zone ${polygonJsIndex + 1}`,
                            polygon: polygonPoints,
                            type: poligon.type || 'polygon',
                            tags: [],
                            alarms: [],
                            armed: [{"start":"00:00:00","stop":"23:59:00","days":[0,1,2,3,4,5,6]}],
                            advanced_settings: {}, // Default empty object, stringified
                            draw_zone: true,
                            scaled_polygon: [],
                            original_width: null,
                            original_height: null,
                            version: null,
                        };
                        const newZone = applyScaledMetadata(baseZone, polygonPoints);
                        return [...prevZones, newZone];
                    }
                } else if (action === 'modified') {
                    // --- UPDATE POINTS FOR MODIFIED ZONE --- (Called directly by polygons.js modifier)
                     if (existingZoneStateIndex !== -1) {
                        console.log(`Updating modified zone points (originalIndex: ${polygonJsIndex}) at state index: ${existingZoneStateIndex}`);
                        const updatedZones = [...prevZones];
                        updatedZones[existingZoneStateIndex] = applyScaledMetadata(updatedZones[existingZoneStateIndex], polygonPoints);
                         return updatedZones;
                     } else {
                         console.warn(`Received 'modified' event for unknown originalIndex: ${polygonJsIndex}`);
                         return prevZones; // No change
                     }

                } else if (action === 'deleted') {
                    // State update is handled by handleDeleteZone
                    console.log('Confirmation: zone deleted event received for React index:', index);
                    return prevZones;
                } else if (action === 'cloned') {
                    // State update is handled by handleCloneZone
                     console.log('Confirmation: zone cloned event received for original React index:', index);
                    return prevZones;
                } else {
                    return prevZones;
                }
            });
        };

        // Listeners for tag updates from editVideoPlayer.js/editTags.js
        const handleAllTagsUpdated = (event) => {
            setAllTags(normalizeToArray(event.detail));
            console.log("Received all tags update:", event.detail);
            // Keep listener active
        };
        const handleTrackedTagsUpdated = (event) => {
            setTrackedTags(normalizeIdList(event.detail));
            console.log("Received tracked tags update:", event.detail);
        }
        const handleSelectedTagsUpdated = (event) => {
            setSelectedTags(normalizeIdList(event.detail));
            console.log("Received selected tags update:", event.detail);
        }
        const handleFixedTagsUpdated = (event) => {
            setFixedTags(normalizeIdList(event.detail));
            console.log("Received fixed tags update:", event.detail);
        }

        const handleSelectedModelUpdated = (event) => {
            setSelectedModel(event.detail?.model || 'multi2_640_300.engine');
            console.log("Received selected model update:", event.detail?.model, event.detail);
            // Optionally refetch all tags for the new model
            fetchAllTags(managerConfig.tagsUrl);
            // get the captioning_flag from the data-captioning
            if (event.detail?.captioning !== undefined) {
                setCaptioningFlag(event.detail.captioning);
            }
        }

        const handleZonesScaled = (event) => {
            const detail = event.detail || {};
            const updatedZones = Array.isArray(detail.zones) ? detail.zones : [];
            const fallbackWidth = detail.width;
            const fallbackHeight = detail.height;

            if (!updatedZones.length) {
                return;
            }

            setZones((prevZones) => prevZones.map((zone, index) => {
                const incoming = updatedZones[index];
                if (!incoming) {
                    return zone;
                }

                const polygon = Array.isArray(incoming.polygon) ? incoming.polygon : zone.polygon;
                const scaledPolygon = Array.isArray(incoming.scaled_polygon) ? incoming.scaled_polygon : zone.scaled_polygon;

                return {
                    ...zone,
                    polygon,
                    scaled_polygon: scaledPolygon,
                    original_width: incoming.original_width ?? fallbackWidth ?? zone.original_width,
                    original_height: incoming.original_height ?? fallbackHeight ?? zone.original_height,
                    version: incoming.version ?? (scaledPolygon?.length ? 2 : zone.version),
                };
            }));
        };

        const handleZoneFocus = (event) => {
            const detailIndex = Number(event.detail?.index);
            if (!Number.isInteger(detailIndex)) {
                return;
            }
            const currentZones = zonesRef.current || [];
            const resolvedIndex = currentZones.findIndex((zone, idx) => {
                const originalIdx = Number(zone?.originalIndex ?? zone?.original_index);
                if (Number.isInteger(originalIdx) && originalIdx === detailIndex) {
                    return true;
                }
                return idx === detailIndex;
            });
            triggerZoneFocus(resolvedIndex !== -1 ? resolvedIndex : detailIndex);
        };

        // Add all listeners
        window.addEventListener("zones:updated", handleZonesUpdated);
        window.addEventListener("allTags:updated", handleAllTagsUpdated);
        window.addEventListener("trackedTags:updated", handleTrackedTagsUpdated);
        window.addEventListener("selectedTags:updated", handleSelectedTagsUpdated);
        window.addEventListener("fixedTags:updated", handleFixedTagsUpdated);
        window.addEventListener("selectedModel:updated", handleSelectedModelUpdated);
        window.addEventListener("zones:scaled", handleZonesScaled);
        window.addEventListener("zones:focus", handleZoneFocus);

        const fixedTagsInput = document.getElementById('input-tags-fixed');
        const syncFixedTagsFromInput = () => {
            if (!fixedTagsInput) {
                return;
            }

            const selectedValues = Array.from(fixedTagsInput.selectedOptions || []).map((option) => option.value);
            setFixedTags(normalizeIdList(selectedValues));
        };

        if (fixedTagsInput) {
            fixedTagsInput.addEventListener('change', syncFixedTagsFromInput);
            syncFixedTagsFromInput();
        }

        const handleZoneReperUpdate = (event) => {
            const zoneIndex = Number(event?.detail?.zoneIndex);
            const nextReper = normalizeZoneReper(event?.detail?.reper);
            if (!Number.isInteger(zoneIndex) || zoneIndex < 0) {
                return;
            }

            setZones((prevZones) => prevZones.map((zone, index) => {
                const nextZone = { ...zone };
                const nextMeta = buildZoneMeta(nextZone);

                if (index === zoneIndex) {
                    nextMeta.reper = nextReper;
                    nextZone.reper = nextReper;
                    nextZone.meta = nextMeta;
                    return nextZone;
                }

                if (nextReper.point) {
                    nextMeta.reper = {};
                    nextZone.reper = {};
                    nextZone.meta = nextMeta;
                }

                return nextZone;
            }));
        };

        const handleZoneReperSelectRequest = (event) => {
            const zoneIndex = Number(event?.detail?.zoneIndex);
            if (!Number.isInteger(zoneIndex) || zoneIndex < 0) {
                return;
            }

            const currentZones = zonesRef.current || [];
            const targetZone = currentZones[zoneIndex];
            if (!targetZone) {
                return;
            }

            if (hasZoneReper(targetZone)) {
                triggerZoneFocus(zoneIndex);
                return;
            }

            handleToggleReperZone(zoneIndex);
        };

        const handleZoneDirectionUpdate = (event) => {
            const zoneIndex = Number(event?.detail?.zoneIndex);
            const nextDirection = normalizeZoneDirection(event?.detail?.direction);
            if (!Number.isInteger(zoneIndex) || zoneIndex < 0) {
                return;
            }

            setZones((prevZones) => prevZones.map((zone, index) => {
                if (index !== zoneIndex) {
                    return zone;
                }

                const nextZone = { ...zone };
                const nextMeta = buildZoneMeta(nextZone);
                nextMeta.direction = nextDirection;
                nextZone.direction = nextDirection;
                nextZone.meta = nextMeta;
                return nextZone;
            }));
        };
        const handleZoneDirectionDelete = (event) => {
            const zoneIndex = Number(event?.detail?.zoneIndex);
            if (!Number.isInteger(zoneIndex) || zoneIndex < 0) {
                return;
            }

            delete lastDirectionByZoneRef.current[zoneIndex];

            setZones((prevZones) => prevZones.map((zone, index) => {
                if (index !== zoneIndex) {
                    return zone;
                }

                const nextZone = { ...zone };
                const nextMeta = buildZoneMeta(nextZone);
                nextMeta.direction = {};
                nextZone.direction = {};
                nextZone.meta = nextMeta;
                return nextZone;
            }));
        };
        window.addEventListener('zones:reper:update', handleZoneReperUpdate);
        window.addEventListener('zones:reper:select-request', handleZoneReperSelectRequest);
        window.addEventListener('zones:direction:update', handleZoneDirectionUpdate);
        window.addEventListener('zones:direction:delete-request', handleZoneDirectionDelete);
        const handleHornSelectionChanged = (event) => {
            const nextHornId = Number.parseInt(event?.detail?.hornId ?? '-1', 10);
            setSelectedHornId(Number.isFinite(nextHornId) ? nextHornId : -1);
            setZoneAudioMessageEnabled(Boolean(event?.detail?.audioMessageEnabled));
        };
        window.addEventListener('item:hornSelectionChanged', handleHornSelectionChanged);

        // Cleanup function
        return () => {
            window.removeEventListener("zones:updated", handleZonesUpdated);
            window.removeEventListener("allTags:updated", handleAllTagsUpdated);
            window.removeEventListener("trackedTags:updated", handleTrackedTagsUpdated);
            window.removeEventListener("selectedTags:updated", handleSelectedTagsUpdated);
            window.removeEventListener("fixedTags:updated", handleFixedTagsUpdated);
            window.removeEventListener("selectedModel:updated", handleSelectedModelUpdated);
            window.removeEventListener("zones:scaled", handleZonesScaled);
            window.removeEventListener("zones:focus", handleZoneFocus);
            window.removeEventListener('zones:reper:update', handleZoneReperUpdate);
            window.removeEventListener('zones:reper:select-request', handleZoneReperSelectRequest);
            window.removeEventListener('zones:direction:update', handleZoneDirectionUpdate);
            window.removeEventListener('zones:direction:delete-request', handleZoneDirectionDelete);
            window.removeEventListener('item:hornSelectionChanged', handleHornSelectionChanged);
            if (fixedTagsInput) {
                fixedTagsInput.removeEventListener('change', syncFixedTagsFromInput);
            }
        };
    }, [fetchAllTags, handleToggleReperZone, managerConfig.tagsUrl, triggerZoneFocus]); // Run once on mount

    // --- Update Zone Data Handler ---
    const handleUpdateZone = (zoneIndex, updatedData) => {
        setZones((prevZones) => {
            const updatedZones = [...prevZones];
            // Ensure we are updating the correct zone in the React state array
            if (zoneIndex >= 0 && zoneIndex < updatedZones.length) {
                 updatedZones[zoneIndex] = { ...updatedZones[zoneIndex], ...updatedData };
                 console.log(`Zone ${zoneIndex} updated:`, updatedZones[zoneIndex]);
            } else {
                console.error(`Invalid zoneIndex (${zoneIndex}) passed to handleUpdateZone.`);
            }
            return updatedZones;
        });
        // TODO: Optionally dispatch an event if legacy code needs to know about general updates?
        // window.dispatchEvent(new CustomEvent("zones:updated", { detail: { action: "updated", index: zoneIndex, zoneData: updatedZones[zoneIndex] } }));
    };

    // --- Advanced Settings Modal Handlers ---
    const handleOpenAdvancedSettings = (zoneIndex) => {
        console.log("Opening advanced settings for zone index:", zoneIndex);
        setAdvancedModalState({
            show: true,
            zoneIndex: zoneIndex,
            zoneData: zones[zoneIndex]
        });
    };

    const handleCloseAdvancedSettings = () => {
        setAdvancedModalState({ show: false, zoneIndex: null, zoneData: null });
    };

    const handleSaveAdvancedSettings = (zoneIndex, newSettings) => {
        console.log("Saving advanced settings for zone:", zoneIndex, "Data:", newSettings);
        // Force the hidden form inputs to reflect the latest zone settings before any submit happens.
        flushSync(() => {
            handleUpdateZone(zoneIndex, {advanced_settings: newSettings});
        });

        // In edit mode, persist zone changes by triggering the existing Save Status flow.
        if (!disabled) {
            requestAnimationFrame(() => {
                const form = document.getElementById('editForm');
                if (!form) {
                    return;
                }

                if (typeof form.requestSubmit === 'function') {
                    form.requestSubmit();
                    return;
                }

                const submitButton = form.querySelector('button[type="submit"]');
                if (submitButton && !submitButton.disabled) {
                    submitButton.click();
                    return;
                }

                form.submit();
            });
        }

        handleCloseAdvancedSettings(); // Close modal after save
    };

    /**
     * Reads the managed alarm rules stored in the hidden Blade input.
     */
    const readAlarmRulesInput = useCallback(() => {
        const alarmsInput = document.getElementById('alarmsData');
        if (!alarmsInput) {
            return [];
        }

        try {
            const parsed = JSON.parse(alarmsInput.value || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.error('Unable to parse existing alarm rules from alarmsData', error);
            return [];
        }
    }, []);

    /**
     * Persists promoted zone indexes so the controller clears legacy simple alarms on save.
     */
    const writePromotedZoneIndexes = useCallback((zoneIndexes = []) => {
        const promotedInput = document.getElementById('promotedSimpleAlarmZones');
        if (!promotedInput) {
            return;
        }

        const normalizedIndexes = Array.from(new Set((zoneIndexes || [])
            .map((value) => Number.parseInt(value, 10))
            .filter((value) => Number.isInteger(value) && value >= 0)))
            .sort((left, right) => left - right);

        promotedInput.value = JSON.stringify(normalizedIndexes);
    }, []);

    /**
     * Builds a managed alarm rule from a legacy simple zone alarm tag.
     */
    const buildManagedRuleFromSimpleZoneAlarm = useCallback((zone, zoneIndex, tagId, ruleId, promotionConfig) => {
        const tag = allTags.find((entry) => String(entry?.id) === String(tagId));
        if (!tag) {
            return null;
        }

        const zoneNumber = zoneIndex + 1;
        const zoneName = zone?.name || `Zone ${zoneNumber}`;
        const alarmFrequency = promotionConfig.alarm_frequency === null
            ? null
            : parseAlarmNumber(promotionConfig.alarm_frequency, 0);

        const rule = {
            version: 1,
            rule_id: ruleId,
            rule: `Alarm for ${tag.name} in ${zoneName}`,
            status: 'active',
            notifications_enabled: true,
            threshold: 0,
            min_duration: parseAlarmNumber(promotionConfig.min_duration, 1),
            text_reasoning: '',
            alarm_frequency: alarmFrequency,
            zone_id: zoneNumber,
            objects: [[tag.name]],
            missing_objects: [],
            displayObjectsData: [{
                classes: [tag.name],
                classes_id: [tag.id],
                missing: false,
                text: `${zoneName}: ${tag.name}`,
                value: tag.id,
            }],
            analysis: [],
            displayAnalysisData: [],
        };
        if (selectedHornId >= 0) {
            const audioMessage = typeof zone?.meta?.audio_message === 'string' ? zone.meta.audio_message : '';
            rule.audio_message = audioMessage.slice(0, 2000);
        }

        return rule;
    }, [allTags, selectedHornId]);

    /**
     * Submits the legacy edit form so promotion follows the existing save-and-reload flow.
     */
    const submitEditForm = useCallback(() => {
        const form = document.getElementById('editForm');
        if (!form) {
            return;
        }

        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
            return;
        }

        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton && !submitButton.disabled) {
            submitButton.click();
            return;
        }

        form.submit();
    }, []);

    /**
     * Reads the persisted simple-alarm conversion metadata used for safe rollback.
     */
    const readSimpleAlarmConversionState = useCallback(() => {
        const stateInput = document.getElementById('simpleAlarmConversionState');
        if (!stateInput || !stateInput.value) {
            return null;
        }

        try {
            const parsed = JSON.parse(stateInput.value || '{}');
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (error) {
            return null;
        }
    }, []);

    /**
     * Canonicalizes nested rule payloads so revert checks can compare them reliably.
     */
    const canonicalizeRuleValue = useCallback((value) => {
        if (Array.isArray(value)) {
            return value.map((entry) => canonicalizeRuleValue(entry));
        }

        if (value && typeof value === 'object') {
            return Object.keys(value)
                .sort()
                .reduce((carry, key) => {
                    carry[key] = canonicalizeRuleValue(value[key]);
                    return carry;
                }, {});
        }

        return value;
    }, []);

    /**
     * Builds the same managed-rule payload used by per-zone promotion for multiple zones at once.
     */
    const buildPromotionPayload = useCallback((zoneEntries, promotionConfig) => {
        const existingRules = readAlarmRulesInput();
        const filteredRules = existingRules.filter((rule) => {
            return !zoneEntries.some(({ zoneIndex, alarmIds }) => {
                const displayObject = Array.isArray(rule?.displayObjectsData) ? rule.displayObjectsData[0] : null;
                const primaryClassId = Array.isArray(displayObject?.classes_id) ? displayObject.classes_id[0] : null;
                const hasAnalysis = Array.isArray(rule?.displayAnalysisData)
                    ? rule.displayAnalysisData.length > 0
                    : Object.keys(rule?.analysis || {}).length > 0;

                return Number(rule?.zone_id) === (zoneIndex + 1)
                    && !displayObject?.missing
                    && !hasAnalysis
                    && alarmIds.some((tagId) => String(primaryClassId ?? '') === String(tagId));
            });
        });

        let nextRuleId = filteredRules
            .map((rule) => parseAlarmNumber(rule?.rule_id ?? rule?.id, 0))
            .reduce((carry, value) => Math.max(carry, value), 0) + 1;

        const generatedRules = [];
        zoneEntries.forEach(({ zone, zoneIndex, alarmIds }) => {
            alarmIds.forEach((tagId) => {
                const rule = buildManagedRuleFromSimpleZoneAlarm(zone, zoneIndex, tagId, nextRuleId, promotionConfig);
                if (rule) {
                    generatedRules.push(rule);
                    nextRuleId += 1;
                }
            });
        });

        return {
            generatedRules,
            mergedRules: [...generatedRules, ...filteredRules],
            zoneIndexes: zoneEntries.map(({ zoneIndex }) => zoneIndex),
        };
    }, [buildManagedRuleFromSimpleZoneAlarm, readAlarmRulesInput]);

    /**
     * Promotes all simple zone alarms in the current flux and submits the page.
     */
    const promoteAllSimpleAlarms = useCallback((promotionConfig) => {
        const currentZones = zonesRef.current || [];
        const zoneEntries = currentZones
            .map((zone, zoneIndex) => ({
                zone,
                zoneIndex,
                alarmIds: normalizeIdList(zone?.alarms),
            }))
            .filter(({ alarmIds }) => alarmIds.length > 0);

        if (!zoneEntries.length) {
            return { ok: false, message: 'No simple alarms are available to convert.' };
        }

        const payload = buildPromotionPayload(zoneEntries, promotionConfig);
        if (!payload.generatedRules.length) {
            return { ok: false, message: 'No simple alarms were promoted.' };
        }

        const alarmsInput = document.getElementById('alarmsData');
        if (alarmsInput) {
            alarmsInput.value = JSON.stringify(payload.mergedRules);
        }

        writePromotedZoneIndexes(payload.zoneIndexes);

        const actionInput = document.getElementById('simpleAlarmConversionAction');
        if (actionInput) {
            actionInput.value = 'promote';
        }

        const clearInput = document.getElementById('clearSimpleAlarmConversion');
        if (clearInput) {
            clearInput.value = '0';
        }

        submitEditForm();
        return { ok: true };
    }, [buildPromotionPayload, submitEditForm, writePromotedZoneIndexes]);

    /**
     * Reverts a whole-flux promotion only when the generated rules are still unchanged.
     */
    const revertPromotedSimpleAlarms = useCallback(() => {
        const conversionState = readSimpleAlarmConversionState();
        if (!conversionState || !Array.isArray(conversionState.generated_rules) || !conversionState.generated_rules.length) {
            return { ok: false, message: 'There is no reversible simple-alarm conversion on this flux.' };
        }

        const currentRules = readAlarmRulesInput();
        const currentRulesById = new Map(
            currentRules.map((rule) => [String(parseAlarmNumber(rule?.rule_id ?? rule?.id, 0)), rule])
        );

        const generatedRuleIds = Array.isArray(conversionState.generated_rule_ids)
            ? conversionState.generated_rule_ids.map((value) => String(parseAlarmNumber(value, 0)))
            : conversionState.generated_rules.map((rule) => String(parseAlarmNumber(rule?.rule_id ?? rule?.id, 0)));

        const canRevert = conversionState.generated_rules.every((expectedRule) => {
            const ruleId = String(parseAlarmNumber(expectedRule?.rule_id ?? expectedRule?.id, 0));
            const currentRule = currentRulesById.get(ruleId);

            return JSON.stringify(canonicalizeRuleValue(currentRule || null))
                === JSON.stringify(canonicalizeRuleValue(expectedRule));
        });

        if (!canRevert) {
            return {
                ok: false,
                clearState: true,
                message: 'These converted alarms were changed in Alarm Management and can no longer be reverted automatically.',
            };
        }

        const remainingRules = currentRules.filter((rule) => {
            const ruleId = String(parseAlarmNumber(rule?.rule_id ?? rule?.id, 0));
            return !generatedRuleIds.includes(ruleId);
        });

        const alarmsInput = document.getElementById('alarmsData');
        if (alarmsInput) {
            alarmsInput.value = JSON.stringify(remainingRules);
        }

        writePromotedZoneIndexes([]);

        const actionInput = document.getElementById('simpleAlarmConversionAction');
        if (actionInput) {
            actionInput.value = 'revert';
        }

        const clearInput = document.getElementById('clearSimpleAlarmConversion');
        if (clearInput) {
            clearInput.value = '0';
        }

        submitEditForm();
        return { ok: true };
    }, [canonicalizeRuleValue, readAlarmRulesInput, readSimpleAlarmConversionState, submitEditForm, writePromotedZoneIndexes]);

    /**
     * Promotes a zone's simple alarms into managed alarm rules and saves the page.
     */
    const handlePromoteSimpleAlarms = useCallback((zoneIndex, promotionConfig) => {
        const currentZones = zonesRef.current || [];
        const zone = currentZones[zoneIndex];
        if (!zone) {
            return;
        }

        const selectedAlarmIds = normalizeIdList(zone?.alarms);
        if (!selectedAlarmIds.length) {
            return;
        }

        const existingRules = readAlarmRulesInput();
        const zoneNumber = zoneIndex + 1;
        const filteredRules = existingRules.filter((rule) => {
            return !selectedAlarmIds.some((tagId) => {
                const displayObject = Array.isArray(rule?.displayObjectsData) ? rule.displayObjectsData[0] : null;
                const primaryClassId = Array.isArray(displayObject?.classes_id) ? displayObject.classes_id[0] : null;
                const hasAnalysis = Array.isArray(rule?.displayAnalysisData) ? rule.displayAnalysisData.length > 0 : Object.keys(rule?.analysis || {}).length > 0;

                return Number(rule?.zone_id) === zoneNumber
                    && !displayObject?.missing
                    && !hasAnalysis
                    && String(primaryClassId ?? '') === String(tagId);
            });
        });

        let nextRuleId = filteredRules
            .map((rule) => parseAlarmNumber(rule?.rule_id ?? rule?.id, 0))
            .reduce((carry, value) => Math.max(carry, value), 0) + 1;

        const promotedRules = selectedAlarmIds
            .map((tagId) => {
                const rule = buildManagedRuleFromSimpleZoneAlarm(zone, zoneIndex, tagId, nextRuleId, promotionConfig);
                if (rule) {
                    nextRuleId += 1;
                }
                return rule;
            })
            .filter(Boolean);

        if (!promotedRules.length) {
            return;
        }

        const alarmsInput = document.getElementById('alarmsData');
        if (alarmsInput) {
            alarmsInput.value = JSON.stringify([...promotedRules, ...filteredRules]);
        }

        const promotedInput = document.getElementById('promotedSimpleAlarmZones');
        const existingPromoted = promotedInput ? (() => {
            try {
                const parsed = JSON.parse(promotedInput.value || '[]');
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                return [];
            }
        })() : [];
        writePromotedZoneIndexes([...existingPromoted, zoneIndex]);
        submitEditForm();
    }, [buildManagedRuleFromSimpleZoneAlarm, readAlarmRulesInput, submitEditForm, writePromotedZoneIndexes]);

    useEffect(() => {
        window.zonesManagerPromoteAllSimpleAlarms = promoteAllSimpleAlarms;
        window.zonesManagerRevertPromotedSimpleAlarms = revertPromotedSimpleAlarms;

        return () => {
            delete window.zonesManagerPromoteAllSimpleAlarms;
            delete window.zonesManagerRevertPromotedSimpleAlarms;
        };
    }, [promoteAllSimpleAlarms, revertPromotedSimpleAlarms]);

    if (isLoading) return <LoadingCard/>;
    if (error) return <div className="alert alert-danger">{error}</div>;

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid item id="zona" sx={{
                display: 'flex',
                flexDirection: expanded ? 'column' : 'row',
                flexWrap: expanded ? 'nowrap' : 'wrap',
                gap: 0,
            }}
                  xs={12} sm={12} md={11} lg={11} xl={10}
            >
                <Grid item xs={12} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography variant="h6" fontWeight={500}>{zones.length} Zones</Typography>
                    <Tooltip title={expanded ? 'Hide Details' : 'Show Full Details'}>
                        <IconButton size="small" onClick={handleToggle}>
                            {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                        </IconButton>
                    </Tooltip>
                </Grid>
                    {zones.map((zone, index) => (
                        <ZoneCard
                            key={index}
                            index={index}
                            zone={zone}
                            disabled={disabled}
                            expanded={expanded}
                            isFocused={focusedZoneIndex === index}
                            focusEventId={focusEventId}
                            onExpandStateChange={(next) => handleCardExpandChange(index, next)}
                            selectedModel={selectedModel}
                            allTags={allTags}
                            markerSets={markerSets}
                            gpioGates={gpioGates}
                            trafficLights={trafficLights}
                            onUpdate={(newZone) => {
                                setZones((zones) =>
                                    zones.map((z, i) => (i === index ? {...z, ...newZone} : z))
                                );
                            }}
                            onDelete={handleDeleteZone}
                            onClone={handleCloneZone}
                            onSchedule={(zoneIndex) => {
                                let zoneData = zones[zoneIndex].armed??[];
                                console.log(zones[zoneIndex]);
                                setArmedData(zoneData);
                                setActiveSchedule(zoneIndex);
                                setScheduleDrawerOpen(true);
                            }}
                            onSettings={(zoneIndex) => {
                                let zoneData = zones[zoneIndex];
                                console.log(zones[zoneIndex]);
                                handleOpenAdvancedSettings(zoneIndex)
                                //window.dispatchEvent(new CustomEvent('zones:settings', { detail: { index: zoneIndex } }));
                            }}
                            onPromoteSimpleAlarms={handlePromoteSimpleAlarms}
                            hidePromotionAction={alarmOverrideEnabled}
                            trackedTags={trackedTags}
                            selectedTags={selectedTags}
                            fixedTags={fixedTags}
                            managerConfig={managerConfig}
                            selectedHornId={selectedHornId}
                            audioMessageEnabled={zoneAudioMessageEnabled}
                            isReperSelected={hasZoneReper(zone)}
                            onToggleReperZone={handleToggleReperZone}
                            isDirectionEnabled={hasZoneDirection(zone)}
                            onToggleDirectionZone={handleToggleDirectionZone}
                            onEditDirectionZone={handleEditDirectionZone}
                        />
                    ))}

                <ScheduleDrawer
                    open={scheduleDrawerOpen}
                    onClose={() => setScheduleDrawerOpen(false)}
                    initialSchedule={armedData}
                    onSave={handleScheduleSave}
                    disabled={disabled}
                />

                {/* Render the Advanced Settings Modal */}
                <ZoneAdvancedSettingsModal
                    open={advancedModalState.show}
                    onClose={handleCloseAdvancedSettings}
                    zoneData={advancedModalState.zoneData}
                    zoneIndex={advancedModalState.zoneIndex}
                    onSave={handleSaveAdvancedSettings}
                    config={managerConfig}
                    allTags={allTags}
                    availableMarkerSets={markerSets}
                />
            </Grid>
        </Grid>
    );
};

export default ZonesManager;
